import React, { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getPlatformAvailabilitySnapshot } from '../services/platformAvailability.ts';

import {
  APP_VERSION_CHECK_INTERVAL_MS,
  type AppVersionCheckResult,
  type ReloadAttemptStorage,
  checkAppVersion,
  forgetReloadAttemptForVersion,
  forgetReloadAttemptsCompletedByVersion,
  getChunkFailureVersion,
  getVersionTransitionReloadKey,
  isChunkLoadError,
  markReloadAttemptedForVersion,
  wasReloadAttemptedForVersion,
} from '../utils/appVersion.ts';

type UpdateReason = 'new-version' | 'chunk-error';

interface PendingUpdate {
  reason: UpdateReason;
  reloadAttemptKey: string;
}

export interface AppVersionGuardProps {
  enabled?: boolean;
  checkIntervalMs?: number;
  reloadAttemptLeaseMs?: number;
  pageExitResetDelayMs?: number;
  versionCheck?: () => Promise<AppVersionCheckResult>;
  reloadPage?: () => void;
  reloadAttemptStorage?: ReloadAttemptStorage | null;
}

const defaultVersionCheck = (): Promise<AppVersionCheckResult> => checkAppVersion();
const defaultReloadPage = (): void => window.location.reload();

const AppVersionGuard: React.FC<AppVersionGuardProps> = ({
  enabled = process.env.NODE_ENV === 'production',
  checkIntervalMs = APP_VERSION_CHECK_INTERVAL_MS,
  reloadAttemptLeaseMs = 5_000,
  pageExitResetDelayMs = 1_000,
  versionCheck = defaultVersionCheck,
  reloadPage = defaultReloadPage,
  reloadAttemptStorage,
}) => {
  const location = useLocation();
  const pendingUpdateRef = useRef<PendingUpdate | null>(null);
  const checkInFlightRef = useRef<Promise<AppVersionCheckResult> | null>(null);
  const reloadRequestedRef = useRef(false);
  const pageExitRef = useRef(false);
  const previousPathnameRef = useRef(location.pathname);
  const reloadAttemptLeaseTimeoutRef = useRef<number | null>(null);
  const pageExitResetTimeoutRef = useRef<number | null>(null);

  const clearPageExitReset = useCallback(() => {
    if (pageExitResetTimeoutRef.current === null) return;
    window.clearTimeout(pageExitResetTimeoutRef.current);
    pageExitResetTimeoutRef.current = null;
  }, []);

  const clearPendingAttempt = useCallback((reloadAttemptKey: string) => {
    if (pendingUpdateRef.current?.reloadAttemptKey === reloadAttemptKey) {
      pendingUpdateRef.current = null;
    }
  }, []);

  const requestReload = useCallback((reloadAttemptKey: string) => {
    if (reloadRequestedRef.current || pageExitRef.current) return;
    const availability = getPlatformAvailabilitySnapshot();
    // Preserva a árvore em edição e operações sem confirmação durante a pausa.
    // Uma próxima navegação segura poderá consumir a atualização pendente.
    if (!availability.ready || availability.pendingCommands > 0) return;

    if (wasReloadAttemptedForVersion(reloadAttemptKey, reloadAttemptStorage)) {
      clearPendingAttempt(reloadAttemptKey);
      return;
    }

    reloadRequestedRef.current = true;

    const attemptPersisted = markReloadAttemptedForVersion(
      reloadAttemptKey,
      reloadAttemptStorage,
    );
    if (!attemptPersisted) {
      // Sem persistência não há como provar que uma nova montagem não criará loop.
      // A tentativa atual falha fechada, mas outra transição pode recuperar o storage.
      reloadRequestedRef.current = false;
      return;
    }

    if (reloadAttemptLeaseTimeoutRef.current !== null) {
      window.clearTimeout(reloadAttemptLeaseTimeoutRef.current);
    }
    reloadAttemptLeaseTimeoutRef.current = window.setTimeout(() => {
      reloadAttemptLeaseTimeoutRef.current = null;

      // Se este código ainda está executando, a saída foi cancelada ou suprimida.
      // Soltar o lease permite uma nova tentativa apenas em outra transição segura.
      forgetReloadAttemptForVersion(reloadAttemptKey, reloadAttemptStorage);
      reloadRequestedRef.current = false;
    }, Math.max(1, reloadAttemptLeaseMs));

    try {
      // Preserva rota, sessão e storage e mantém o beforeunload nativo.
      reloadPage();
    } catch {
      // O lease acima recupera silenciosamente uma tentativa rejeitada pelo browser.
    }
  }, [
    clearPendingAttempt,
    reloadAttemptLeaseMs,
    reloadAttemptStorage,
    reloadPage,
  ]);

  const queueUpdate = useCallback((
    reloadAttemptKey: string,
    reason: UpdateReason,
    immediate = false,
  ) => {
    if (wasReloadAttemptedForVersion(reloadAttemptKey, reloadAttemptStorage)) return;

    pendingUpdateRef.current = { reason, reloadAttemptKey };

    if (immediate && !pageExitRef.current) {
      requestReload(reloadAttemptKey);
    }
  }, [reloadAttemptStorage, requestReload]);

  const runVersionCheck = useCallback(async () => {
    if (!checkInFlightRef.current) {
      checkInFlightRef.current = versionCheck().finally(() => {
        checkInFlightRef.current = null;
      });
    }

    try {
      const result = await checkInFlightRef.current;
      if (!result.loaded || !result.latest) {
        return;
      }

      const hasVersionMismatch = result.loaded.id !== result.latest.id;
      if (result.updateAvailable !== hasVersionMismatch) {
        return;
      }

      if (!forgetReloadAttemptsCompletedByVersion(
        result.loaded.id,
        reloadAttemptStorage,
      )) {
        return;
      }

      // Uma falha de chunk precisa continuar sendo a prioridade: ela pode ser a
      // única forma de recuperar uma tela cujo bundle parcial deixou de carregar.
      if (pendingUpdateRef.current?.reason === 'chunk-error') {
        return;
      }

      if (!hasVersionMismatch) {
        pendingUpdateRef.current = null;
        return;
      }

      const reloadAttemptKey = getVersionTransitionReloadKey(
        result.loaded.id,
        result.latest.id,
      );
      if (wasReloadAttemptedForVersion(reloadAttemptKey, reloadAttemptStorage)) {
        pendingUpdateRef.current = null;
        return;
      }

      pendingUpdateRef.current = {
        reason: 'new-version',
        reloadAttemptKey,
      };
    } catch {
      // Falhas transitórias serão verificadas novamente no próximo evento/intervalo.
    }
  }, [reloadAttemptStorage, versionCheck]);

  useEffect(() => {
    if (!enabled) return undefined;

    void runVersionCheck();

    const resumePage = () => {
      clearPageExitReset();
      pageExitRef.current = false;
    };
    const handleFocus = () => {
      if (pageExitRef.current) return;
      void runVersionCheck();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resumePage();
      }

      if (!pageExitRef.current) {
        void runVersionCheck();
      }
    };
    const handleBeforeUnload = () => {
      pageExitRef.current = true;
      clearPageExitReset();
      pageExitResetTimeoutRef.current = window.setTimeout(() => {
        pageExitResetTimeoutRef.current = null;
        pageExitRef.current = false;
      }, Math.max(1, pageExitResetDelayMs));
    };
    const handlePageHide = () => {
      pageExitRef.current = true;
      clearPageExitReset();
    };
    const handlePageShow = () => {
      resumePage();
      void runVersionCheck();
    };
    const reloadAfterChunkFailure = () => {
      // A recuperação não depende da consulta de versão, que pode estar indisponível.
      queueUpdate(getChunkFailureVersion(), 'chunk-error', true);
    };
    const handleWindowError = (event: Event) => {
      const errorEvent = event as ErrorEvent;
      if (isChunkLoadError(errorEvent.error) || isChunkLoadError(errorEvent.message)) {
        reloadAfterChunkFailure();
      } else if (isChunkLoadError(event)) {
        reloadAfterChunkFailure();
      }
    };
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isChunkLoadError(event.reason)) {
        reloadAfterChunkFailure();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('error', handleWindowError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    const intervalId = window.setInterval(() => {
      void runVersionCheck();
    }, checkIntervalMs);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('error', handleWindowError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.clearInterval(intervalId);
      clearPageExitReset();
      if (reloadAttemptLeaseTimeoutRef.current !== null) {
        window.clearTimeout(reloadAttemptLeaseTimeoutRef.current);
        reloadAttemptLeaseTimeoutRef.current = null;
      }
    };
  }, [
    checkIntervalMs,
    clearPageExitReset,
    enabled,
    pageExitResetDelayMs,
    queueUpdate,
    requestReload,
    runVersionCheck,
  ]);

  useEffect(() => {
    const pathnameChanged = previousPathnameRef.current !== location.pathname;
    previousPathnameRef.current = location.pathname;

    if (!enabled || !pathnameChanged) return;

    const pendingUpdate = pendingUpdateRef.current;
    if (pendingUpdate && !pageExitRef.current) {
      requestReload(pendingUpdate.reloadAttemptKey);
    }
  }, [enabled, location.pathname, requestReload]);

  return null;
};

export default AppVersionGuard;
